import type { Assertion, PropertyDefinition, PropertySummary } from '@peruselab/api-client/assertions';
import { isCurrent, type MoleculeProperties } from '../chemistry/molecule/useMoleculeProperties';

/** Whether the current values of a property say the same thing, compared in SI (ADR 0018 D4). */
export type Agreement = 'single' | 'agree' | 'differ';

/** #929: one card in the Monograph's Properties grid -- the preferred value and how it stands. */
export interface PropertyCard {
  readonly property: PropertyDefinition;
  /** The preferred value (D9), shown large. */
  readonly preferred: Assertion | null;
  readonly count: number;
  /** How many current values cite an accepted source (standing SOURCED). */
  readonly sourced: number;
  readonly agreement: Agreement;
  /** How far apart the current values are, in SI (largest minus smallest). */
  readonly spread: number;
  readonly summary: PropertySummary | undefined;
  /** The preferred value's source, short ("HSDB"), or null when it has none to name. */
  readonly source: string | null;
}

/** Without a catalog tolerance: "the same value" -- 275 °F and 135 °C are both 408.15 K, give or take rounding. */
const SAME_VALUE = 1e-6;

/** "Hazardous Substances Data Bank (HSDB)" -> "HSDB"; a name without an abbreviation stays whole. */
export function shortSourceName(name: string): string {
  const abbreviation = /\(([A-Z][A-Za-z0-9&-]{1,15})\)\s*$/.exec(name);
  return abbreviation ? abbreviation[1]! : name;
}

function sourceOf(assertion: Assertion): string | null {
  if (assertion.imported) return shortSourceName(assertion.imported.source);
  const reference = assertion.references[0];
  if (!reference) return null;
  return shortSourceName(reference.venue ?? reference.title ?? reference.identifier);
}

function spreadOf(values: readonly Assertion[]): { readonly min: number; readonly max: number } {
  return {
    min: Math.min(...values.map((value) => value.siValue)),
    max: Math.max(...values.map((value) => value.siUpper ?? value.siValue))
  };
}

/** #931: whether values agree within the property's catalog tolerance (in SI). */
export function agreementOf(values: readonly Assertion[], property: PropertyDefinition): Agreement {
  if (values.length < 2) return 'single';
  const { min, max } = spreadOf(values);
  const tolerance = property.agreement;
  if (tolerance?.kind === 'ABSOLUTE') return max - min <= tolerance.tolerance + SAME_VALUE ? 'agree' : 'differ';
  const scale = Math.max(Math.abs(min), Math.abs(max), Number.MIN_VALUE);
  const allowed = tolerance?.kind === 'RELATIVE' ? tolerance.tolerance : SAME_VALUE;
  return (max - min) / scale <= allowed + SAME_VALUE ? 'agree' : 'differ';
}

/** Every property with at least one current value, in catalog order, as a card. */
export function propertyCards(loaded: MoleculeProperties): PropertyCard[] {
  const current = loaded.assertions.filter(isCurrent);
  return loaded.catalog.flatMap((property) => {
    const values = current.filter((assertion) => assertion.propertyId === property.id);
    if (values.length === 0) return [];
    const summary = loaded.summaries.find((candidate) => candidate.propertyId === property.id);
    const eligible = values.filter((value) => !value.problemReport);
    const preferred =
      eligible.find((value) => value.id === summary?.preferredId) ?? eligible[eligible.length - 1] ?? null;
    return [
      {
        property,
        preferred,
        count: values.length,
        sourced: values.filter((value) => value.standing === 'SOURCED').length,
        agreement: agreementOf(values, property),
        spread: (({ min, max }) => max - min)(spreadOf(values)),
        summary,
        source: preferred ? sourceOf(preferred) : null
      }
    ];
  });
}

/** "At a glance": the first property whose sources all agree, if any (two or more values). */
export function agreedProperty(cards: readonly PropertyCard[]): PropertyCard | null {
  return cards.find((card) => card.preferred !== null && card.agreement === 'agree') ?? null;
}
